// src/components/features/brands/forms/BrandLogoUpload.jsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import SecureAvatar from '../../../components/ui/SecureAvatar';

const BrandLogoUpload = ({ 
  title, 
  description, 
  logo, 
  onUpload, 
  onRemove, 
  isUploading = false,
  maxSize = 200, // Maximum display size in pixels
  acceptedFormats = 'image/*',
  error = false,
  required = false
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Handle file selection via click
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onUpload(e.dataTransfer.files[0]);
    }
  };

  // Determine if we have a logo to display
  const hasLogo = Boolean(logo && (logo.url || logo.key));
  
  // Get the URL to display
  // const displayUrl = logo ? (logo.key ? logo.key : logo || '') : '';

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        border: `1px solid ${alpha(
          error ? theme.palette.error.main : 
          dragActive ? theme.palette.primary.main : 
          theme.palette.divider, 
          error ? 0.5 : 0.1
        )}`,
        borderColor: error ? theme.palette.error.main : 
                    dragActive ? theme.palette.primary.main : 
                    alpha(theme.palette.divider, 0.1),
        transition: 'all 0.2s ease-in-out',
        backgroundColor: error ? alpha(theme.palette.error.main, 0.05) : theme.palette.background.paper,
        '&:hover': {
          boxShadow: theme.shadows[3],
        },
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {title}
          {required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </Box>

      {/* Logo Preview or Upload Area */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          flexGrow: 1,
          backgroundColor: alpha(
            error ? theme.palette.error.main : 
            theme.palette.background.default, 
            error ? 0.03 : 0.5
          ),
          borderRadius: '8px',
          border: `1px dashed ${
            error ? theme.palette.error.main : 
            dragActive ? theme.palette.primary.main : 
            alpha(theme.palette.text.disabled, 0.3)
          }`,
          position: 'relative',
        }}
      >
        {isUploading ? (
          // Loading state
          <CircularProgress size={36} />
        ) : hasLogo ? (
          // Logo preview with buttons to replace and remove
          <Box sx={{ position: 'relative', textAlign: 'center' }}>
            <SecureAvatar
              src={logo}
              alt={title}
              sx={{
                width: maxSize,
                height: maxSize,
                mb: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                maxWidth: maxSize,
                maxHeight: maxSize,
                display: 'block',
                margin: '0 auto',
              }}
            />
            {/* Remove button (top right) */}
            <IconButton
              color="error"
              sx={{
                position: 'absolute',
                top: -12,
                right: -12,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.light, 0.1),
                },
              }}
              onClick={onRemove}
              size="small"
              title="Remove logo"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            
            {/* Replace button (top left) */}
            <IconButton
              color="primary"
              sx={{
                position: 'absolute',
                top: -12,
                left: -12,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.light, 0.1),
                },
              }}
              onClick={() => fileInputRef.current.click()}
              size="small"
              title="Replace logo"
            >
              <UploadIcon fontSize="small" />
            </IconButton>
            
            <Typography variant="caption" display="block" mt={1}>
              {logo.originalname || 'Brand Logo'}
            </Typography>
          </Box>
        ) : (
          // Upload prompt
          <Box sx={{ textAlign: 'center' }}>
            <UploadIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Drag &amp; drop or click to upload
            </Typography>
            <Typography variant="caption" color="textSecondary">
              PNG, JPG, SVG (max 2MB)
            </Typography>
          </Box>
        )}
      </Box>

      {/* Upload/Replace button */}
      <Button
        variant="outlined"
        startIcon={hasLogo ? <UploadIcon /> : <UploadIcon />}
        onClick={() => fileInputRef.current.click()}
        sx={{ mt: 2 }}
        fullWidth
        color={hasLogo ? "primary" : "primary"}
      >
        {hasLogo ? 'Replace Logo' : 'Upload Logo'}
      </Button>
      
      {/* Delete button (only shown if there's a logo) */}
      {hasLogo && (
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={onRemove}
          sx={{ mt: 1 }}
          fullWidth
          color="error"
        >
          Remove Logo
        </Button>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={acceptedFormats}
        style={{ display: 'none' }}
      />
    </Paper>
  );
};

export default BrandLogoUpload;